import type { ReactElement } from 'react';
import type { TopicDefinition } from './TopicCatalogTypes';
import type { AdHocAggregateResult, AdHocTrendResult } from '@/shared/analytics/AnalyticsTypes';
import { sliceToPath } from './TopicSliceKey';
import { formatBucketLabel, formatCount, metricLabel } from '@/shared/analytics/MetricFormatting';
import { isResolvedKey } from '@/shared/analytics/AnalyticsTypes';
import HeatMap, { type HeatMapCell, type HeatMapColumn, type HeatMapRow } from '@/design/HeatMap/HeatMap';

interface TimeMatrixVizProps {
    definition: TopicDefinition;
    primary: AdHocAggregateResult;
    trend: AdHocTrendResult;
    basePath: string;
}

/**
 * Time-series heatmap. The primary field's top values become rows, successive
 * time buckets become columns, and cell intensity encodes the headline metric
 * per (value × bucket), normalised onto {@link HeatMap}. The whole row drills
 * into that value's slice (the time dimension is dropped on drill-in).
 */
export default function TimeMatrixViz({ definition, primary, trend, basePath }: TimeMatrixVizProps): ReactElement {
    const rowLimit = definition.matrixRowLimit ?? 8;
    const rowValues = primary.rows.slice(0, rowLimit).map((row) => row.keys[definition.primaryField] ?? '—');
    const rowSet = new Set(rowValues);
    const timeBucket = definition.timeBucket ?? 'week';

    const bucketSet = new Set<string>();
    const cells = new Map<string, Map<string, number>>();
    const rowTotals = new Map<string, number>();
    let maxCell = 0;
    for (const point of trend.points) {
        const rowValue = point.key ?? '—';
        if (!rowSet.has(rowValue)) continue;
        bucketSet.add(point.bucket);
        const value = point.metrics[definition.headlineMetric];
        const byBucket = cells.get(rowValue) ?? new Map<string, number>();
        byBucket.set(point.bucket, value);
        cells.set(rowValue, byBucket);
        rowTotals.set(rowValue, (rowTotals.get(rowValue) ?? 0) + value);
        if (value > maxCell) maxCell = value;
    }
    const buckets = [...bucketSet].sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));

    const rowHref = (rowValue: string): string | undefined => (isResolvedKey(rowValue) ? sliceToPath(basePath, definition.id, { primary: rowValue }) : undefined);

    const rows: HeatMapRow[] = rowValues.map((rowValue) => ({
        key: rowValue,
        label: rowValue,
        href: rowHref(rowValue),
        total: rowTotals.get(rowValue) ?? 0,
    }));
    const columns: HeatMapColumn[] = buckets.map((bucket) => ({
        key: bucket,
        label: formatBucketLabel(bucket, timeBucket),
    }));
    const cellAt = (rowKey: string, bucket: string): HeatMapCell => ({
        value: cells.get(rowKey)?.get(bucket) ?? 0,
        href: rowHref(rowKey),
    });

    return (
        <HeatMap
            rows={rows}
            columns={columns}
            cellAt={cellAt}
            maxValue={maxCell}
            accent={definition.accent}
            cornerLabel={definition.lensLabel}
            metricNoun={metricLabel(definition.headlineMetric).toLowerCase()}
            formatValue={formatCount}
            columnMinWidth={40}
        />
    );
}
